/* eslint-disable @typescript-eslint/naming-convention */
import { NodeId, TreeViewNode, TraitLabel, StableNodeId } from '@fluid-experimental/tree';

import { MfsCollection, MfsItem, MfsItemId, MfsRelationship } from '../../store/mfsTypes';
import { MfsValue } from '../../store/mfsValue';

/**
 * The definitions of the tree nodes used in Mfs.
 *
 * @example
 *   The hierarchy of nodes on a sample Mfs Collection item.
 *   //Item
 *   // |_values:
 *   // |     |__Reference
 *   // |     |__Reference
 *   // |     |__..
 *   // |
 *   // |_property1:
 *   // |      |__SingleValue
 *   // |
 *   // |_property2:
 *   // |      |__Array
 *   // |         |__MultiValue
 *   // |         |__MultiValue
 *   // |         |__...
 *   // |
 *   // |_relationship1:
 *   //        |__EdgeSet
 *   //           |__Edge
 *   //           |__Edge
 *   //           |__..
 */
export const enum NodeKind {
  /** Mfs Item node. */
  Item = 'i',
  /** Reference to an item in a collection. */
  Reference = 'r',
  /** Multi-value property node. */
  Array = 'a',
  /** Each value of a multi-value property. Always a child of Array node. */
  MultiValueNode = 'mv',
  /** The value of a single-value property. Always a child of the Item node. */
  SingleValueNode = 'sv',
  /** Edge node. */
  Edge = 'e',
  /** An edge set. */
  EdgeSet = 'es',
  /** Mfs Item index node. */
  Index = 'x',
}

export type NodeTraits = TreeViewNode['traits'];

/** TreeViewNode without `payload` to use in the type-definitions for Mfs nodes with type-safe payloads. */
export type MfsNodeBase = Omit<TreeViewNode, 'payload'>;

export const ITEMS = 'items' as TraitLabel;

/** Represents the collection values property. */
export const COLLECTION_VALUES = 'values' as TraitLabel;

/** Represent the property values of a multi-value property node. */
export const PROPERTY_VALUES = 'av' as TraitLabel;

export type NodeTypeGuard<T extends TreeViewNode> = (node: TreeViewNode) => node is T;

export type MfsReferenceNode = MfsNodeBase & {
  payload: MfsItemId;
};

/** Represents an item node which can be a single item or collection. */
export type MfsItemNode = MfsNodeBase & {
  payload: Pick<MfsItem, 'label' | 'id'> & Partial<Pick<MfsCollection, 'kind'>>;
};

/** The node that stores the id of an MfsItemNode in payload. */
export type MfsIndexNode = MfsNodeBase & {
  payload: StableNodeId;
};

/** The non-editable base properties of items are saved in item node payload. */
export const MfsItemNodeProperties: Record<keyof MfsItemNode['payload'], boolean> = {
  kind: true,
  label: true,
  id: true,
};

/** Represents an item node of type Mfs Collection. */
export type MfsCollectionNode = MfsNodeBase & {
  payload: Pick<MfsCollection, 'label' | 'kind' | 'id'>;
};

/** Represents a value of a single-value property or each value of a multi-value property. */
export type MfsValueNode = MfsNodeBase & {
  payload: MfsValue;
};

/**
 * Represents a multi-value property. The values of the properties are {@link MfsValueNode}. We could have saved the
 * MfsValue nodes directly on the PROPERTY_VALUES trait of the item node, but there's no way to represent/preserve empty
 * array values in the tree (there cannot be a trait with empty NodeIds), hence we have this additional node.
 */
export type MfsMultiValueNode = Omit<TreeViewNode, 'traits'> & {
  readonly traits: ReadonlyMap<typeof PROPERTY_VALUES, readonly NodeId[]>;
};

/** Represents a directed edge of a given source node. */
export type MfsEdgeData = {
  /** The unique type of the edge. */
  type: MfsRelationship['type'];

  /** The id of the item that's adjacent to the source node. */
  neighbor: MfsItemId;

  /**
   * Represents the direction of the edge:
   *
   * - If `in`, the edge is incoming to the source node.
   * - If `out`, the edge is outgoing from the source node.
   */
  dir: MfsRelationship['direction'];

  /** Custom attributes, if any specified by the app. */
  attributes?: MfsRelationship['attributes'];
};

/** The edge node that has the edge data in payload. */
export type MfsEdgeNode = MfsNodeBase & {
  payload: MfsEdgeData;
};

/** The parenf of the edge nodes which ensures that a unique edge per related item exists. */
export type MfsEdgeSetNode = MfsNodeBase & {
  payload: undefined;
};

/** Whether the property is replaced (put) or delta-updated (patch). */
export type PropertySetMode = 'put' | 'patch';
